import { sendTrackingEvent } from 'actions/workflows/workflows';
import type {
  ForethoughtLiveChatHandoffComponent,
  LiveChatIntegration,
  SuncoLiveChatComponent,
} from 'api/types';
import { isPusherLiveChatIntegration } from 'api/types';
import { useAppDispatch } from 'hooks';
import { useEffect } from 'react';
import {
  setIntegrationId,
  setLiveChatIntegration,
  setLiveChatId,
  setUserEndedChat,
  setLiveChatIdAndStatus,
} from 'reducers/live-chat';

const useHandleLiveChatSetup = ({
  conversationId,
  setStepSent,
  widgetComponent,
}: {
  conversationId: string;
  setStepSent: (query?: string) => void;
  widgetComponent: ForethoughtLiveChatHandoffComponent | SuncoLiveChatComponent;
}) => {
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!widgetComponent.isSent) {
      const isSuncoLiveChat =
        widgetComponent.component_type === 'sunco_live_chat';

      const integration: LiveChatIntegration = isSuncoLiveChat
        ? 'sunco'
        : widgetComponent.component_fields.integration_config.integration;

      /**
       * sunco tracking event is in `useHandleSuncoInitialization`
       *
       * zendesk zopim tracking event is in `useHandleZendeskChatInitialization`
       */
      const sendHandoffTrackingEvent = (integration: LiveChatIntegration) => {
        if (isSuncoLiveChat) {
          return;
        }

        if (isPusherLiveChatIntegration(integration)) {
          dispatch(
            sendTrackingEvent({
              conversation_id: conversationId,
              event_type: 'widget-customer-handoff',
              helpdesk_conversation_id:
                widgetComponent.component_fields.helpdesk_conversation_id,
              integration: `${integration}-live-chat`,
              success: true,
            }),
          );
        }
      };

      if (isSuncoLiveChat) {
        /**
         * Sunco needs to setup before setting status to pending
         */
        dispatch(setLiveChatIntegration('sunco'));
        dispatch(
          setIntegrationId(
            widgetComponent.component_fields.web_messenger_integration_id,
          ),
        );
      } else {
        dispatch(setLiveChatIntegration(integration));
        switch (integration) {
          case 'kustomer':
            /**
             * Kustomer calls setLiveChatId in useHandleKustomerChatInitialization
             */
            dispatch(setUserEndedChat(false));
            break;
          case 'zendesk':
            /**
             * Zendesk needs to do the availability check before setting to pending
             */
            dispatch(
              setLiveChatId(
                widgetComponent.component_fields.helpdesk_conversation_id,
              ),
            );
            break;
          case 'freshchat':
          case 'intercom':
            dispatch(
              setLiveChatIdAndStatus({
                chatId:
                  widgetComponent.component_fields.helpdesk_conversation_id,
                status: 'pending',
              }),
            );
            break;
        }
      }

      sendHandoffTrackingEvent(integration);

      if (integration !== 'zendesk') {
        // zopim one chat does this in initZopimChat
        setStepSent();
      }
    }
  }, [dispatch, setStepSent, widgetComponent, conversationId]);
};

export default useHandleLiveChatSetup;
